import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/api';
import './PosPage.css';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
];

const emptyCustomer = {
  customer_name: '',
  customer_phone: '',
  customer_addr_line1: '',
  customer_addr_line2: '',
  customer_city: '',
  customer_state: '',
  customer_pincode: '',
};

const PosPage = () => {
  const [carts, setCarts] = useState([]);
  const [activeCartId, setActiveCartId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Inventory search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Payment panel
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [particulars, setParticulars] = useState('');
  const [customerCredits, setCustomerCredits] = useState([]);
  const [selectedCreditCodes, setSelectedCreditCodes] = useState([]);
  const [creditLoading, setCreditLoading] = useState(false);

  // Promo
  const [promoInput, setPromoInput] = useState('');

  // Order search / returns
  const [orderSearchPhone, setOrderSearchPhone] = useState('');
  const [orderSearchDate, setOrderSearchDate] = useState('');
  const [orderResults, setOrderResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderLoading, setSelectedOrderLoading] = useState(false);
  const [returnSelections, setReturnSelections] = useState({}); // order_item_id -> qty
  const [refundMode, setRefundMode] = useState('cash');
  const [returnNote, setReturnNote] = useState('');
  const [lastOrder, setLastOrder] = useState(null); // last completed POS order (for invoice print)

  const activeCart = carts.find((c) => c.id === activeCartId) || null;
  const selectedCredits = customerCredits.filter((credit) => selectedCreditCodes.includes(credit.credit_code));
  const creditApplied = selectedCredits.reduce(
    (applied, credit) => Math.min(applied + Number(credit.remaining_amount || 0), Number(activeCart?.total_amount || 0)),
    0
  );
  const balanceDue = Math.max(Number(activeCart?.total_amount || 0) - creditApplied, 0);

  const fetchCarts = useCallback(async () => {
    try {
      const res = await axios.get('/api/pos/carts', { params: { status_filter: 'open' } });
      setCarts(res.data);
      if (!activeCartId && res.data.length > 0) {
        setActiveCartId(res.data[0].id);
      } else if (activeCartId && !res.data.find((c) => c.id === activeCartId)) {
        setActiveCartId(res.data.length > 0 ? res.data[0].id : null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load carts');
    }
  }, [activeCartId]);

  useEffect(() => {
    fetchCarts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeCart) {
      setPromoInput(activeCart.promo_code || '');
      setPaymentAmount(activeCart.total_amount || '');
    }
  }, [activeCartId, carts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeCart) {
      setPaymentAmount(balanceDue.toFixed(2));
    }
  }, [activeCart, balanceDue]);

  const showNotice = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 4000);
  };

  // Fetch the auth-protected invoice HTML and open it in a printable window.
  const printInvoice = async (orderId) => {
    try {
      const res = await axios.get(`/api/pos/orders/${orderId}/invoice`, { responseType: 'text' });
      const win = window.open('', '_blank');
      if (!win) {
        setError('Popup blocked — please allow popups to print the invoice');
        return;
      }
      win.document.open();
      win.document.write(res.data);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 400);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load invoice');
    }
  };

  // ---------- Cart management ----------
  const createCart = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/pos/carts', { ...emptyCustomer });
      await fetchCarts();
      setActiveCartId(res.data.id);
      showNotice('New cart opened');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create cart');
    } finally {
      setLoading(false);
    }
  };

  const updateCustomer = async (field, value) => {
    if (!activeCart) return;
    const updated = { ...activeCart, [field]: value };
    setCarts(carts.map((c) => (c.id === activeCart.id ? updated : c)));
  };

  const saveCustomer = async () => {
    if (!activeCart) return;
    try {
      await axios.put(`/api/pos/carts/${activeCart.id}`, {
        customer_name: activeCart.customer_name,
        customer_phone: activeCart.customer_phone,
        customer_addr_line1: activeCart.customer_addr_line1,
        customer_addr_line2: activeCart.customer_addr_line2,
        customer_city: activeCart.customer_city,
        customer_state: activeCart.customer_state,
        customer_pincode: activeCart.customer_pincode,
      });
      showNotice('Customer saved');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save customer');
    }
  };

  const voidCart = async () => {
    if (!activeCart) return;
    if (!window.confirm('Void this cart? This cannot be undone.')) return;
    try {
      await axios.post(`/api/pos/carts/${activeCart.id}/void`);
      await fetchCarts();
      showNotice('Cart voided');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to void cart');
    }
  };

  // ---------- Inventory ----------
  const searchInventory = async (term) => {
    setSearchTerm(term);
    if (!term || term.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get('/api/pos/inventory/search', { params: { q: term, limit: 20 } });
      setSearchResults(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const addItem = async (result) => {
    if (!activeCart) {
      setError('Open a cart first');
      return;
    }
    try {
      const res = await axios.post(`/api/pos/carts/${activeCart.id}/items`, {
        product_id: result.product_id,
        variant_id: result.variant_id,
        quantity: 1,
      });
      setCarts(carts.map((c) => (c.id === activeCart.id ? res.data : c)));
      setSearchTerm('');
      setSearchResults([]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add item');
    }
  };

  const updateItemQty = async (item, qty) => {
    if (qty < 1) return;
    try {
      const res = await axios.put(`/api/pos/carts/${activeCart.id}/items/${item.id}`, { quantity: qty });
      setCarts(carts.map((c) => (c.id === activeCart.id ? res.data : c)));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update quantity');
    }
  };

  const removeItem = async (item) => {
    try {
      const res = await axios.delete(`/api/pos/carts/${activeCart.id}/items/${item.id}`);
      setCarts(carts.map((c) => (c.id === activeCart.id ? res.data : c)));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to remove item');
    }
  };

  // ---------- Promo ----------
  const applyPromo = async () => {
    if (!activeCart) return;
    try {
      const res = await axios.post(`/api/pos/carts/${activeCart.id}/promo`, { promo_code: promoInput });
      setCarts(carts.map((c) => (c.id === activeCart.id ? res.data : c)));
      showNotice(promoInput ? 'Promo applied' : 'Promo removed');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to apply promo');
    }
  };

  // ---------- Checkout ----------
  const checkout = async () => {
    if (!activeCart) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`/api/pos/carts/${activeCart.id}/checkout`, {
        payment_method: paymentMethod,
        amount: paymentAmount ? parseFloat(paymentAmount) : null,
        transaction_id: transactionId || null,
        particulars: particulars || null,
        credit_codes: selectedCreditCodes,
      });
      showNotice(`Order ${res.data.invoice_number} completed — ₹${res.data.total_amount} (${res.data.credit_applied > 0 ? `credit ₹${res.data.credit_applied}, ` : ''}${res.data.payment_method} ₹${res.data.balance_paid})`);
      setLastOrder(res.data);
      setTransactionId('');
      setParticulars('');
      setCustomerCredits([]);
      setSelectedCreditCodes([]);
      setPaymentMethod('cash');
      await fetchCarts();
    } catch (e) {
      setError(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerCredits = async (phone = activeCart?.customer_phone) => {
    const normalizedPhone = (phone || '').trim();
    if (!normalizedPhone) {
      setError('Enter or reuse a customer phone number before loading store credits');
      return;
    }
    setCreditLoading(true);
    try {
      const res = await axios.get('/api/pos/credits', { params: { phone: normalizedPhone } });
      setCustomerCredits(Array.isArray(res.data) ? res.data : []);
      setSelectedCreditCodes([]);
    } catch (e) {
      setCustomerCredits([]);
      setSelectedCreditCodes([]);
      setError(e.response?.data?.detail || 'Failed to load customer store credits');
    } finally {
      setCreditLoading(false);
    }
  };

  const toggleCredit = (creditCode, selected) => {
    setSelectedCreditCodes((current) => (
      selected ? [...current, creditCode] : current.filter((code) => code !== creditCode)
    ));
  };

  // ---------- Order search & returns ----------
  const searchOrders = async () => {
    try {
      const params = {};
      if (orderSearchPhone) params.phone = orderSearchPhone;
      if (orderSearchDate) { params.date_from = orderSearchDate; params.date_to = orderSearchDate; }
      const res = await axios.get('/api/pos/orders/search', { params });
      setOrderResults(res.data);
      setSelectedOrder(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Order search failed');
    }
  };

  const selectPreviousOrder = async (orderId) => {
    setSelectedOrderLoading(true);
    setError('');
    try {
      const res = await axios.get(`/api/pos/orders/${orderId}/details`);
      setSelectedOrder(res.data);
      setReturnSelections({});
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load order details');
    } finally {
      setSelectedOrderLoading(false);
    }
  };

  const reuseSelectedCustomer = async () => {
    if (!activeCart || !selectedOrder) return;
    const customer = {
      customer_name: selectedOrder.shipping_contact_name || '',
      customer_isd_code: selectedOrder.shipping_contact_isd_code || '+91',
      customer_phone: selectedOrder.shipping_contact_number || '',
      customer_addr_line1: selectedOrder.shipping_addr_line1 || '',
      customer_addr_line2: selectedOrder.shipping_addr_line2 || '',
      customer_city: selectedOrder.shipping_city || '',
      customer_state: selectedOrder.shipping_state || '',
      customer_pincode: String(selectedOrder.shipping_pincode || ''),
    };
    try {
      const res = await axios.put(`/api/pos/carts/${activeCart.id}`, customer);
      setCarts((current) => current.map((cart) => (cart.id === activeCart.id ? res.data : cart)));
      await loadCustomerCredits(customer.customer_phone);
      showNotice('Customer details copied to the active cart');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reuse customer details');
    }
  };

  const findPreviousCustomer = async () => {
    if (!activeCart) return;
    const phone = (activeCart.customer_phone || '').trim();
    if (!phone) {
      setError('Enter a customer phone number to find previous details');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const searchRes = await axios.get('/api/pos/orders/search', { params: { phone, limit: 1 } });
      const previousOrder = Array.isArray(searchRes.data) ? searchRes.data[0] : null;
      if (!previousOrder) {
        setCustomerCredits([]);
        setSelectedCreditCodes([]);
        showNotice('No previous POS order found for this phone number');
        return;
      }

      const detailRes = await axios.get(`/api/pos/orders/${previousOrder.id}/details`);
      const customer = {
        customer_name: detailRes.data.shipping_contact_name || '',
        customer_isd_code: detailRes.data.shipping_contact_isd_code || '+91',
        customer_phone: detailRes.data.shipping_contact_number || phone,
        customer_addr_line1: detailRes.data.shipping_addr_line1 || '',
        customer_addr_line2: detailRes.data.shipping_addr_line2 || '',
        customer_city: detailRes.data.shipping_city || '',
        customer_state: detailRes.data.shipping_state || '',
        customer_pincode: String(detailRes.data.shipping_pincode || ''),
      };
      const cartRes = await axios.put(`/api/pos/carts/${activeCart.id}`, customer);
      setCarts((current) => current.map((cart) => (cart.id === activeCart.id ? cartRes.data : cart)));
      await loadCustomerCredits(customer.customer_phone);
      showNotice(`Customer details reused from ${detailRes.data.invoice_number || 'the most recent order'}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to find previous customer details');
    } finally {
      setLoading(false);
    }
  };

  const toggleReturnItem = (item, checked) => {
    const next = { ...returnSelections };
    if (checked) next[item.id] = item.quantity;
    else delete next[item.id];
    setReturnSelections(next);
  };

  const setReturnQty = (itemId, qty) => {
    setReturnSelections({ ...returnSelections, [itemId]: qty });
  };

  const processReturn = async () => {
    if (!selectedOrder) return;
    const items = Object.entries(returnSelections).map(([order_item_id, quantity]) => ({ order_item_id, quantity }));
    if (items.length === 0) {
      setError('Select at least one item to return');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post('/api/pos/returns', {
        order_id: selectedOrder.id,
        items,
        refund_mode: refundMode,
        note: returnNote || null,
      });
      const creditMsg = res.data.credit_code ? ` — Credit ${res.data.credit_code} (₹${res.data.credit_amount})` : '';
      showNotice(`Return processed — refund ₹${res.data.refund_total}${creditMsg}`);
      setReturnSelections({});
      setSelectedOrder(null);
      searchOrders();
    } catch (e) {
      setError(e.response?.data?.detail || 'Return failed');
    } finally {
      setLoading(false);
    }
  };

  const cartLabel = (c) => c.customer_name || c.customer_phone || `Cart ${c.id.slice(0, 6)}`;

  return (
    <div className="pos-page">
      <header className="pos-header">
        <h1>Sales Desk (POS)</h1>
        <button className="pos-btn primary" onClick={createCart} disabled={loading}>
          + New Cart
        </button>
      </header>

      {error && <div className="pos-alert error" onClick={() => setError('')}>{error}</div>}
      {notice && <div className="pos-alert success">{notice}</div>}
      {lastOrder && (
        <div className="pos-alert success pos-invoice-banner">
          <span>Order <strong>{lastOrder.invoice_number}</strong> completed — ₹{lastOrder.total_amount} ({lastOrder.payment_method})</span>
          <button className="pos-btn primary sm" onClick={() => printInvoice(lastOrder.order_id)}>🖨 Print Invoice</button>
          <button className="pos-btn sm" onClick={() => setLastOrder(null)}>Dismiss</button>
        </div>
      )}

      <div className="pos-layout">
        {/* Left: open carts tabs + active cart */}
        <section className="pos-carts">
          <div className="pos-cart-tabs">
            {carts.map((c) => (
              <button
                key={c.id}
                className={`pos-tab ${c.id === activeCartId ? 'active' : ''}`}
                onClick={() => setActiveCartId(c.id)}
              >
                {cartLabel(c)}
                <span className="pos-tab-total">₹{c.total_amount}</span>
              </button>
            ))}
            {carts.length === 0 && <p className="pos-empty">No open carts. Click "+ New Cart".</p>}
          </div>

          {activeCart && (
            <div className="pos-cart-detail">
              {/* Customer */}
              <div className="pos-card">
                <h3>Customer</h3>
                <div className="pos-grid">
                  <input placeholder="Name" value={activeCart.customer_name || ''} onChange={(e) => updateCustomer('customer_name', e.target.value)} onBlur={saveCustomer} />
                  <input placeholder="Phone" value={activeCart.customer_phone || ''} onChange={(e) => updateCustomer('customer_phone', e.target.value)} onBlur={saveCustomer} />
                  <input placeholder="Address line 1" value={activeCart.customer_addr_line1 || ''} onChange={(e) => updateCustomer('customer_addr_line1', e.target.value)} onBlur={saveCustomer} />
                  <input placeholder="Address line 2" value={activeCart.customer_addr_line2 || ''} onChange={(e) => updateCustomer('customer_addr_line2', e.target.value)} onBlur={saveCustomer} />
                  <input placeholder="City" value={activeCart.customer_city || ''} onChange={(e) => updateCustomer('customer_city', e.target.value)} onBlur={saveCustomer} />
                  <input placeholder="State" value={activeCart.customer_state || ''} onChange={(e) => updateCustomer('customer_state', e.target.value)} onBlur={saveCustomer} />
                  <input placeholder="Pincode" value={activeCart.customer_pincode || ''} onChange={(e) => updateCustomer('customer_pincode', e.target.value)} onBlur={saveCustomer} />
                </div>
                <div className="pos-customer-lookup">
                  <button className="pos-btn" onClick={findPreviousCustomer} disabled={loading || !(activeCart.customer_phone || '').trim()}>
                    Find Previous Customer
                  </button>
                  <span>Searches the phone number and reuses the most recent customer details.</span>
                </div>
              </div>

              {/* Items */}
              <div className="pos-card">
                <h3>Items ({activeCart.items?.length || 0})</h3>
                {activeCart.items?.length === 0 && <p className="pos-empty">Search inventory to add items.</p>}
                {activeCart.items?.map((item) => (
                  <div key={item.id} className="pos-line-item">
                    <div className="pos-line-info">
                      <strong>{item.product_name}</strong>
                      <span className="pos-muted">{item.variant_name}</span>
                    </div>
                    <div className="pos-line-qty">
                      <button onClick={() => updateItemQty(item, item.quantity - 1)} disabled={item.quantity <= 1}>−</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateItemQty(item, item.quantity + 1)} disabled={item.quantity >= item.available_stock}>+</button>
                    </div>
                    <div className="pos-line-price">₹{item.line_total}</div>
                    <button className="pos-btn danger sm" onClick={() => removeItem(item)}>✕</button>
                  </div>
                ))}
              </div>

              {/* Promo + totals */}
              <div className="pos-card">
                <div className="pos-promo-row">
                  <input placeholder="Promo code" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} />
                  <button className="pos-btn" onClick={applyPromo}>Apply</button>
                </div>
                <div className="pos-totals">
                  <div><span>Subtotal</span><span>₹{activeCart.subtotal_amount}</span></div>
                  <div><span>GST</span><span>₹{activeCart.gst_amount}</span></div>
                  {activeCart.promo_code && <div className="pos-promo-applied"><span>Promo {activeCart.promo_code}</span></div>}
                  <div className="pos-grand"><span>Total</span><span>₹{activeCart.total_amount}</span></div>
                </div>
              </div>

              {/* Payment */}
              <div className="pos-card pos-payment">
                <h3>Payment</h3>
                <div className="pos-grid">
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                    {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <input type="number" step="0.01" placeholder="Amount (default = total)" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
                  <input placeholder="Transaction ID" value={transactionId} onChange={(e) => setTransactionId(e.target.value)} />
                  <input placeholder="Particulars (card/UPI ref, etc.)" value={particulars} onChange={(e) => setParticulars(e.target.value)} />
                </div>
                <div className="pos-credit-summary">
                  <button className="pos-btn sm" onClick={() => loadCustomerCredits()} disabled={creditLoading}>Load Customer Store Credits</button>
                  {creditLoading ? <span>Checking store credits...</span> : customerCredits.length === 0 ? <span>No active store credits loaded.</span> : (
                    <div className="pos-credit-options">
                      {customerCredits.map((credit) => (
                        <label key={credit.id}>
                          <input type="checkbox" checked={selectedCreditCodes.includes(credit.credit_code)} onChange={(event) => toggleCredit(credit.credit_code, event.target.checked)} />
                          {credit.credit_code}: ₹{credit.remaining_amount} available
                        </label>
                      ))}
                    </div>
                  )}
                  <span>Applied ₹{creditApplied.toFixed(2)}; balance due ₹{balanceDue.toFixed(2)}.</span>
                </div>
                <div className="pos-actions">
                  <button className="pos-btn primary lg" onClick={checkout} disabled={loading || !activeCart.items?.length}>
                    Collect & Close — ₹{paymentAmount || balanceDue.toFixed(2)}
                  </button>
                  <button className="pos-btn danger" onClick={voidCart} disabled={loading}>Void Cart</button>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Right: inventory search */}
        <section className="pos-inventory">
          <div className="pos-card">
            <h3>Add Items</h3>
            <input
              className="pos-search"
              placeholder="Search by product name or SKU..."
              value={searchTerm}
              onChange={(e) => searchInventory(e.target.value)}
            />
            {searching && <p className="pos-muted">Searching...</p>}
            <div className="pos-search-results">
              {searchResults.map((r) => (
                <div key={r.variant_id} className="pos-search-item" onClick={() => addItem(r)}>
                  {r.thumbnail_url && <img src={r.thumbnail_url} alt="" />}
                  <div className="pos-line-info">
                    <strong>{r.product_name}</strong>
                    <span className="pos-muted">{r.variant_name} · {r.variant_sku}</span>
                  </div>
                  <div className={`pos-stock ${r.available_stock <= 0 ? 'out' : ''}`}>
                    {r.available_stock <= 0 ? 'Out' : `Qty ${r.available_stock}`}
                  </div>
                  <div className="pos-line-price">₹{r.final_per_unit}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Order search & returns */}
      <section className="pos-returns">
        <div className="pos-card">
          <h3>Find Previous Order / Returns</h3>
          <div className="pos-search-row">
            <input placeholder="Customer phone" value={orderSearchPhone} onChange={(e) => setOrderSearchPhone(e.target.value)} />
            <input type="date" value={orderSearchDate} onChange={(e) => setOrderSearchDate(e.target.value)} />
            <button className="pos-btn" onClick={searchOrders}>Search</button>
          </div>

          {orderResults.length > 0 && (
            <div className="pos-order-results">
              {orderResults.map((o) => (
                <div key={o.id} className={`pos-order-row ${selectedOrder?.id === o.id ? 'selected' : ''}`} onClick={() => selectPreviousOrder(o.id)}>
                  <div><strong>{o.invoice_number || o.id.slice(0, 8)}</strong></div>
                  <div>{o.shipping_contact_name}</div>
                  <div>{o.shipping_contact_number}</div>
                  <div>{new Date(o.created_at).toLocaleDateString()}</div>
                  <div>₹{o.total_amount}</div>
                  <div className="pos-muted">{o.status}</div>
                </div>
              ))}
            </div>
          )}

          {selectedOrder && (
            <div className="pos-order-detail">
              <div className="pos-order-detail-head">
                <h4>Order {selectedOrder.invoice_number} — items</h4>
                <div className="pos-actions">
                  <button className="pos-btn sm" onClick={reuseSelectedCustomer} disabled={!activeCart}>Use This Customer</button>
                  <button className="pos-btn sm" onClick={() => printInvoice(selectedOrder.id)}>🖨 Print Invoice</button>
                </div>
              </div>
              {selectedOrderLoading ? <p className="pos-muted">Loading order details...</p> : (
                <>
                  {(selectedOrder.return_refund_mode || selectedOrder.return_received_note) && (
                    <div className="pos-return-summary">
                      <strong>Return</strong>
                      <span>Refund mode: {selectedOrder.return_refund_mode || '-'}</span>
                      <span>Note / reason: {selectedOrder.return_received_note || '-'}</span>
                    </div>
                  )}
                  {(selectedOrder.issued_credits || []).map((credit) => (
                    <div className="pos-credit-issued" key={credit.id}>
                      <strong>Store credit {credit.credit_code}</strong>
                      <span>Issued ₹{credit.amount}; available ₹{credit.remaining_amount}; {credit.status}</span>
                      {credit.note && <span>Note: {credit.note}</span>}
                    </div>
                  ))}
                  {(selectedOrder.payment_transactions || []).length > 0 && (
                    <div className="pos-transaction-list">
                      <strong>Payment Transactions</strong>
                      {(selectedOrder.payment_transactions || []).map((payment) => (
                        <div className="pos-transaction-row" key={payment.id}>
                          <span>{new Date(payment.created_at).toLocaleString()}</span>
                          <span>{payment.event_type} / {payment.status}</span>
                          <span>{payment.payment_method || '-'}</span>
                          <span>₹{payment.amount}</span>
                          <span>{payment.transaction_id || payment.particulars || '-'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {selectedOrder.items?.map((item) => (
                <div key={item.id} className="pos-line-item">
                  <input
                    type="checkbox"
                    checked={!!returnSelections[item.id]}
                    onChange={(e) => toggleReturnItem(item, e.target.checked)}
                    disabled={item.status !== 'active'}
                  />
                  <div className="pos-line-info">
                    <strong>{item.product_name}</strong>
                    <span className="pos-muted">{item.size_name || ''} · qty {item.quantity} · {item.status}</span>
                  </div>
                  {returnSelections[item.id] && (
                    <input
                      type="number" min="1" max={item.quantity}
                      value={returnSelections[item.id]}
                      onChange={(e) => setReturnQty(item.id, Math.min(parseInt(e.target.value) || 1, item.quantity))}
                      className="pos-return-qty"
                    />
                  )}
                  <div className="pos-line-price">₹{item.subtotal || item.price}</div>
                </div>
              ))}
              <div className="pos-return-controls">
                <select value={refundMode} onChange={(e) => setRefundMode(e.target.value)}>
                  <option value="cash">Refund in Cash</option>
                  <option value="credit">Issue Store Credit / Exchange</option>
                </select>
                <input placeholder="Note / reason" value={returnNote} onChange={(e) => setReturnNote(e.target.value)} />
                <button className="pos-btn primary" onClick={processReturn} disabled={loading || Object.keys(returnSelections).length === 0}>
                  Process Return
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PosPage;
